// @ts-expect-error
import { jitsiLocalStorage } from '@jitsi/js-utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isMobileBrowser } from '../../../base/environment/utils';
import { getLocalParticipant, isLocalParticipantModerator } from '../../../base/participants/functions';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import { isReactionsButtonEnabled, shouldDisplayReactionsButtons } from '../../../reactions/functions.web';
import { hideToolbox, setHangupMenuVisible, setOverflowMenuVisible, setToolbarHovered, showToolbox } from '../../actions.web';
import { getJwtDisabledButtons, getVisibleButtons, isButtonEnabled, isToolboxAlwaysVisible, isToolboxVisible } from '../../functions.web';
import { useKeyboardShortcuts, useToolboxButtons } from '../../hooks.web';
import HangupButton from '../HangupButton';
import { getConferenceName } from '../../../base/conference/functions';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import ConferenceTimer from '../../../conference/components/ConferenceTimer';
import { BETA_BUTTONS } from '../../constants';
import { EndConferenceButton } from './EndConferenceButton';
import HangupMenuButton from './HangupMenuButton';
import { LeaveConferenceButton } from './LeaveConferenceButton';
import OverflowMenuButton from './OverflowMenuButton';
import Separator from './Separator';
import { setTileViewDimensions, setVerticalViewDimensions } from '../../../filmstrip/actions.web';
import { getCurrentLayout, isLayoutTileView } from '../../../video-layout/functions.any';
import { rerenderLargeVideo } from '../../../large-video/actions.web';
import { LAYOUTS } from '../../../video-layout/constants';
import ClickableIcon from '../../../base/ui/components/web/ClickableIcon';
import { IconPinLabel, IconUnpinLabel } from '../../../base/icons/svg';
import { overwriteConfig } from '../../../base/config/actions';
const useStyles = makeStyles()(() => {
    return {
        contextMenu: {
            position: 'relative',
            right: 'auto',
            margin: 0,
            marginBottom: '8px',
            maxHeight: 'calc(100dvh - 100px)',
            minWidth: '240px'
        },
        hangupMenu: {
            position: 'relative',
            right: 'auto',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '8px',
            margin: 0,
            padding: '16px',
            marginBottom: '4px'
        }
    };
});
/**
 * A component that renders the main toolbar.
 *
 * @param {IProps} props - The props of the component.
 * @returns {ReactElement}
 */
export default function Toolbox({ toolbarButtons }) {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const _toolboxRef = useRef(null);
    const conference = useSelector((state) => state['features/base/conference'].conference);
    const isNarrowLayout = useSelector((state) => state['features/base/responsive-ui'].isNarrowLayout);
    const clientWidth = useSelector((state) => state['features/base/responsive-ui'].clientWidth);
    const isModerator = useSelector(isLocalParticipantModerator);
    const customToolbarButtons = useSelector((state) => state['features/base/config'].customToolbarButtons);
    const iAmRecorder = useSelector((state) => state['features/base/config'].iAmRecorder);
    const iAmSipGateway = useSelector((state) => state['features/base/config'].iAmSipGateway);
    const overflowDrawer = useSelector((state) => state['features/toolbox'].overflowDrawer);
    const shiftUp = useSelector((state) => state['features/toolbox'].shiftUp);
    const overflowMenuVisible = useSelector((state) => state['features/toolbox'].overflowMenuVisible);
    const hangupMenuVisible = useSelector((state) => state['features/toolbox'].hangupMenuVisible);
    const buttonsWithNotifyClick = useSelector((state) => state['features/toolbox'].buttonsWithNotifyClick);
    const reduxToolbarButtons = useSelector((state) => state['features/toolbox'].toolbarButtons);
    const toolbarButtonsToUse = toolbarButtons || reduxToolbarButtons;
    const chatOpen = useSelector((state) => state['features/chat'].isOpen);
    const isDialogVisible = useSelector((state) => Boolean(state['features/base/dialog'].component));
    const jwt = useSelector((state) => state['features/base/jwt'].jwt);
    const localParticipant = useSelector(getLocalParticipant);
    const jwtDisabledButtons = getJwtDisabledButtons(jwt, localParticipant?.features);
    const reactionsButtonEnabled = useSelector(isReactionsButtonEnabled);
    const _shouldDisplayReactionsButtons = useSelector(shouldDisplayReactionsButtons);
    const toolbarVisible = useSelector(isToolboxVisible);
    const isTileView = useSelector(isLayoutTileView);
    const mainToolbarButtonsThresholds = useSelector((state) => state['features/toolbox'].mainToolbarButtonsThresholds);
    const subToolbarButtonsThresholds = useSelector((state) => state['features/toolbox'].subToolbarButtonsThresholds);
    const subject = useSelector(getConferenceName);
    const allButtons = useToolboxButtons(customToolbarButtons);
    const isShowLabel = useSelector((state) => state['features/toolbox'].isShowLabel);
    const _currentLayout = useSelector(getCurrentLayout);
    const toolbarConfig = useSelector((state) => state['features/base/config'].toolbarConfig);
    const alwaysVisible = useSelector((state) => isToolboxAlwaysVisible(state));
    const { showPrejoin: isInPrejoin } = useSelector((state) => state["features/prejoin"]);
    const [initToolbarState, setInitToolbarState] = useState(false);
    useKeyboardShortcuts(toolbarButtonsToUse);
    useEffect(() => {
        if (!initToolbarState && !alwaysVisible && !isInPrejoin) {
            setInitToolbarState(true);
            dispatch(hideToolbox(false, true));
        }
    }, [isInPrejoin, initToolbarState]);
    useEffect(() => {
        dispatch(rerenderLargeVideo());
        if (!toolbarVisible) {
            if (document.activeElement instanceof HTMLElement
                && _toolboxRef.current?.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        }
        if (isTileView)
            dispatch(setTileViewDimensions());
        if (_currentLayout === LAYOUTS.VERTICAL_FILMSTRIP_VIEW)
            dispatch(setVerticalViewDimensions());
    }, [toolbarVisible]);
    /**
     * Sets the visibility of the hangup menu.
     *
     * @param {boolean} visible - Whether or not the hangup menu should be
     * displayed.
     * @private
     * @returns {void}
     */
    const onSetHangupVisible = useCallback((visible) => {
        dispatch(setHangupMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, [dispatch]);
    /**
     * Sets the visibility of the overflow menu.
     *
     * @param {boolean} visible - Whether or not the overflow menu should be
     * displayed.
     * @private
     * @returns {void}
     */
    const onSetOverflowVisible = useCallback((visible) => {
        dispatch(setOverflowMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, [dispatch]);
    useEffect(() => {
        if (hangupMenuVisible && !toolbarVisible) {
            onSetHangupVisible(false);
            dispatch(setToolbarHovered(false));
        }
    }, [dispatch, hangupMenuVisible, toolbarVisible, onSetHangupVisible]);
    useEffect(() => {
        if (overflowMenuVisible && isDialogVisible) {
            onSetOverflowVisible(false);
            dispatch(setToolbarHovered(false));
        }
        if (overflowMenuVisible) {
            const highlightElm = document.querySelector('.toolbox-button-wth-dialog');
            if (highlightElm) {
                highlightElm.classList.remove('show-save-popover');
            }
        }
    }, [dispatch, overflowMenuVisible, isDialogVisible, onSetOverflowVisible]);
    /**
     * Key handler for overflow/hangup menus.
     *
     * @param {KeyboardEvent} e - Esc key click to close the popup.
     * @returns {void}
     */
    const onEscKey = useCallback((e) => {
        if (e?.key === 'Escape') {
            e?.stopPropagation();
            hangupMenuVisible && dispatch(setHangupMenuVisible(false));
            overflowMenuVisible && dispatch(setOverflowMenuVisible(false));
        }
    }, [dispatch, hangupMenuVisible, overflowMenuVisible]);
    /**
     * Dispatches an action signaling the toolbar is not being hovered.
     *
     * @private
     * @returns {void}
     */
    const onMouseOut = useCallback(() => {
        !overflowMenuVisible && dispatch(setToolbarHovered(false));
    }, [dispatch, overflowMenuVisible]);
    /**
     * Dispatches an action signaling the toolbar is being hovered.
     *
     * @private
     * @returns {void}
     */
    const onMouseOver = useCallback(() => {
        dispatch(setToolbarHovered(true));
    }, [dispatch]);
    /**
     * Toggle the toolbar visibility when tabbing into it.
     *
     * @returns {void}
     */
    const onTabIn = useCallback(() => {
        if (!toolbarVisible) {
            dispatch(showToolbox());
        }
    }, [toolbarVisible, dispatch]);
    const togglePinToolbar = () => {
        const newValue = !alwaysVisible;
        jitsiLocalStorage.setItem("toolbarAlwaysVisible", JSON.stringify(newValue));
        dispatch(overwriteConfig({
            toolbarConfig: {
                ...toolbarConfig,
                alwaysVisible: newValue
            }
        }));
        if (newValue) {
            dispatch(showToolbox());
        }
        else {
            dispatch(hideToolbox());
        }
    };
    if (iAmRecorder || iAmSipGateway) {
        return null;
    }
    const endConferenceSupported = Boolean(conference?.isEndConferenceSupported() && isModerator);
    const isMobile = isMobileBrowser();
    const rootClassNames = `new-toolbox ${toolbarVisible ? 'visible' : ''} ${toolbarButtonsToUse.length ? '' : 'no-buttons'} ${chatOpen ? 'shift-right' : ''}`;
    const toolbarAccLabel = 'toolbar.accessibilityLabel.moreActionsMenu';
    const containerClassName = `toolbox-content${isMobile || isNarrowLayout ? ' toolbox-content-mobile' : ''}`;
    const { mainMenuButtons, overflowMenuButtons, subMenuButtons, subButtonsKeys } = getVisibleButtons({
        allButtons,
        buttonsWithNotifyClick,
        toolbarButtons: toolbarButtonsToUse,
        clientWidth,
        jwtDisabledButtons,
        mainToolbarButtonsThresholds,
        subToolbarButtonsThresholds
    });
    const raiseHandInOverflowMenu = overflowMenuButtons.some(({ key }) => key === 'raisehand');
    const showReactionsInOverflowMenu = _shouldDisplayReactionsButtons
        && ((!reactionsButtonEnabled && (raiseHandInOverflowMenu || isNarrowLayout || isMobile))
            || overflowMenuButtons.some(({ key }) => key === 'reactions'));
    const showRaiseHandInReactionsMenu = showReactionsInOverflowMenu && raiseHandInOverflowMenu;
    return (React.createElement("div", { className: cx(rootClassNames, shiftUp && 'shift-up'), id: 'new-toolbox' },
        React.createElement("div", { className: containerClassName },
            React.createElement("div", { className: 'toolbox-content-wrapper', onFocus: onTabIn, ...(isMobile ? {} : {
                    onMouseOut,
                    onMouseOver
                }) },
                React.createElement("div", { className: 'toolbox-content-items', ref: _toolboxRef },
                    !toolbarButtons && React.createElement("div", { className: 'meeting-info' },
                        React.createElement("div", { className: "time-meeting" },
                            React.createElement(ConferenceTimer, null)),
                        React.createElement(Tooltip, { alwaysVisible: true, containerClassName: 'tooltip-name-meeting', content: subject },
                            React.createElement("div", { className: "name-meeting" }, subject))),
                    React.createElement("div", { className: 'main-buttons' },
                        mainMenuButtons.filter(button => !subButtonsKeys.includes(button.key)).map(({ Content, key, ...rest }) => Content !== Separator && (React.createElement(Content, { ...rest, buttonKey: key, key: key, showLabel: !toolbarButtons && isShowLabel, customClass: 'item-toolbox-custom', isShowTooltip: true, isBeta: BETA_BUTTONS.includes(key) }))),
                        !toolbarButtons && Boolean(overflowMenuButtons.length) && (React.createElement(OverflowMenuButton, { ariaControls: 'overflow-menu', buttons: overflowMenuButtons.reduce((acc, val) => {
                                if (val.key === 'reactions' && showReactionsInOverflowMenu) {
                                    return acc;
                                }
                                if (val.key === 'raisehand' && showRaiseHandInReactionsMenu) {
                                    return acc;
                                }
                                if (acc.length) {
                                    const prev = acc[acc.length - 1];
                                    const group = prev[prev.length - 1].group;
                                    if (group === val.group) {
                                        prev.push(val);
                                    }
                                    else {
                                        acc.push([val]);
                                    }
                                }
                                else {
                                    acc.push([val]);
                                }
                                return acc;
                            }, []), isOpen: overflowMenuVisible, key: 'overflow-menu', onToolboxEscKey: onEscKey, onVisibilityChange: onSetOverflowVisible, showRaiseHandInReactionsMenu: showRaiseHandInReactionsMenu, showReactionsMenu: showReactionsInOverflowMenu, showLabel: !toolbarButtons && isShowLabel, isShowTooltip: !isShowLabel })),
                        isButtonEnabled('hangup', toolbarButtonsToUse) && (endConferenceSupported
                            ? React.createElement(HangupMenuButton, { ariaControls: 'hangup-menu', isOpen: hangupMenuVisible, key: 'hangup-menu', notifyMode: buttonsWithNotifyClick?.get('hangup-menu'), onVisibilityChange: onSetHangupVisible, showLabel: isShowLabel, isShowTooltip: !isShowLabel },
                                React.createElement(ContextMenu, { accessibilityLabel: t(toolbarAccLabel), className: classes.hangupMenu, hidden: false, inDrawer: overflowDrawer, onKeyDown: onEscKey },
                                    React.createElement(EndConferenceButton, { buttonKey: 'end-meeting', notifyMode: buttonsWithNotifyClick?.get('end-meeting') }),
                                    React.createElement(LeaveConferenceButton, { buttonKey: 'hangup', notifyMode: buttonsWithNotifyClick?.get('hangup') })))
                            : React.createElement(HangupButton, { showLabel: isShowLabel, isShowTooltip: !isShowLabel, buttonKey: 'hangup', customClass: 'hangup-button hangup-menu-button item-toolbox-custom', key: 'hangup-button', notifyMode: buttonsWithNotifyClick.get('hangup'), visible: isButtonEnabled('hangup', toolbarButtonsToUse) }))),
                    !toolbarButtons && React.createElement("div", { className: 'sub-buttons' },
                        subMenuButtons.map(({ Content, key, ...rest }) => Content !== Separator && (React.createElement(Content, { ...rest, buttonKey: key, key: key, showLabel: !toolbarButtons && isShowLabel, isShowTooltip: !isShowLabel, customClass: 'item-toolbox-custom' }))),
                        React.createElement(Tooltip, { position: 'left', containerClassName: 'box-handle-label', content: alwaysVisible ? t("toolbar.hideLableButton") : t("toolbar.showLabelButton") },
                            React.createElement(ClickableIcon, { className: 'pin-toolbar', accessibilityLabel: alwaysVisible ? t("toolbar.hideLableButton") : t("toolbar.showLabelButton"), onClick: togglePinToolbar, icon: alwaysVisible ? IconUnpinLabel : IconPinLabel }))))))));
}
